import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * LogoSlider Component - Professional infinite scrolling carousel
 * @param {Array} items - Array of logo objects with icon/src and label
 * @param {boolean} autoPlay - Enable auto scroll (default: true)
 * @param {number} speed - Scroll speed in px/s (default: 120)
 * @param {string} direction - Scroll direction: 'left' | 'right' (default: 'left')
 * @param {boolean} pauseOnHover - Pause animation on hover (default: true)
 * @param {string} variant - 'icon' or 'image' (default: 'icon')
 * @param {string} className - Additional CSS classes
 */

const ANIMATION_CONFIG = {
  SMOOTH_TAU: 0.25,
  MIN_COPIES: 2,
  COPY_HEADROOM: 2,
  ELEMENT_WIDTH: 96 // Фиксированная ширина одного элемента (изображение + gap)
};

const cx = (...parts) => parts.filter(Boolean).join(' ');

const useResizeObserver = (callback, elements, dependencies) => {
  useEffect(() => {
    if (!window.ResizeObserver) {
      const handleResize = () => callback();
      window.addEventListener('resize', handleResize);
      callback();
      return () => window.removeEventListener('resize', handleResize);
    }

    const observers = elements.map(ref => {
      if (!ref.current) return null;
      const observer = new ResizeObserver(callback);
      observer.observe(ref.current);
      return observer;
    });

    callback();

    return () => {
      observers.forEach(observer => observer?.disconnect());
    };
  }, dependencies);
};

const useImageLoader = (seqRef, onLoad, dependencies) => {
  useEffect(() => {
    const images = seqRef.current?.querySelectorAll('img') ?? [];

    if (images.length === 0) {
      onLoad();
      return;
    }

    let remainingImages = images.length;
    const handleImageLoad = () => {
      remainingImages -= 1;
      if (remainingImages === 0) {
        onLoad();
      }
    };

    images.forEach(img => {
      if (img.complete) {
        handleImageLoad();
      } else {
        img.addEventListener('load', handleImageLoad, { once: true });
        img.addEventListener('error', handleImageLoad, { once: true });
      }
    });

    return () => {
      images.forEach(img => {
        img.removeEventListener('load', handleImageLoad);
        img.removeEventListener('error', handleImageLoad);
      });
    };
  }, dependencies);
};

const useAnimationLoop = (
  trackRef,
  targetVelocity,
  seqWidth,
  isHovered
) => {
  const rafRef = useRef(null);
  const lastTimestampRef = useRef(null);
  const offsetRef = useRef(0);
  const velocityRef = useRef(0);
  const lastSeqWidthRef = useRef(0);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    const prefersReduced =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Обнуляем позицию при изменении seqWidth
    if (seqWidth > 0 && lastSeqWidthRef.current !== seqWidth) {
      lastSeqWidthRef.current = seqWidth;
      offsetRef.current = 0;
      velocityRef.current = 0;
      lastTimestampRef.current = null;
    }

    if (seqWidth > 0) {
      offsetRef.current = ((offsetRef.current % seqWidth) + seqWidth) % seqWidth;
      track.style.transform = `translate3d(${-offsetRef.current}px, 0, 0)`;
    }

    if (prefersReduced) {
      track.style.transform = 'translate3d(0, 0, 0)';
      return () => {
        lastTimestampRef.current = null;
      };
    }

    const animate = (timestamp) => {
      if (lastTimestampRef.current === null) {
        lastTimestampRef.current = timestamp;
      }

      const deltaTime = Math.max(0, timestamp - lastTimestampRef.current) / 1000;
      lastTimestampRef.current = timestamp;

      const target = isHovered ? 0 : targetVelocity;
      const easingFactor = 1 - Math.exp(-deltaTime / ANIMATION_CONFIG.SMOOTH_TAU);
      velocityRef.current += (target - velocityRef.current) * easingFactor;

      if (seqWidth > 0) {
        let nextOffset = offsetRef.current + velocityRef.current * deltaTime;
        nextOffset = ((nextOffset % seqWidth) + seqWidth) % seqWidth;
        offsetRef.current = nextOffset;
        track.style.transform = `translate3d(${-offsetRef.current}px, 0, 0)`;
      }

      rafRef.current = requestAnimationFrame(animate);
    };

    rafRef.current = requestAnimationFrame(animate);

    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      lastTimestampRef.current = null;
    };
  }, [targetVelocity, seqWidth, isHovered]);
};

const LogoSlider = React.memo(
  ({ items = [], autoPlay = true, speed = 120, direction = 'left', pauseOnHover = true, variant = 'icon', className = '', style = {} }) => {
    const containerRef = useRef(null);
    const trackRef = useRef(null);
    const seqRef = useRef(null);

    const [seqWidth, setSeqWidth] = useState(0);
    const [copyCount, setCopyCount] = useState(ANIMATION_CONFIG.MIN_COPIES);
    const [isHovered, setIsHovered] = useState(false);

    const speedMultiplier = direction === 'right' ? -1 : 1;
    const targetVelocity = Math.abs(speed) * speedMultiplier;

    // Рассчитываем ширину на основе количества элементов
    // Каждый элемент: 96px + 32px gap, последний элемент получает доп 32px (итого 64px)
    const calculatedSeqWidth = useMemo(() => {
      if (items.length === 0) return 0;
      return items.length * ANIMATION_CONFIG.ELEMENT_WIDTH + 32; // +32 за последний элемент (64px вместо 32px)
    }, [items.length]);

    const updateDimensions = useCallback(() => {
      const containerWidth = containerRef.current?.clientWidth ?? 0;

      if (calculatedSeqWidth > 0) {
        setSeqWidth(calculatedSeqWidth);
        const copiesNeeded = Math.ceil(containerWidth / calculatedSeqWidth) + ANIMATION_CONFIG.COPY_HEADROOM;
        setCopyCount(Math.max(ANIMATION_CONFIG.MIN_COPIES, copiesNeeded));
      }
    }, [calculatedSeqWidth]);

    useResizeObserver(updateDimensions, [containerRef], [calculatedSeqWidth]);
    useAnimationLoop(trackRef, autoPlay ? targetVelocity : 0, seqWidth, isHovered && pauseOnHover);

    const logoLists = useMemo(
      () =>
        Array.from({ length: copyCount }, (_, copyIndex) => (
          <ul
            className="flex items-center"
            key={`copy-${copyIndex}`}
            role="list"
            aria-hidden={copyIndex > 0}
            ref={copyIndex === 0 ? seqRef : undefined}
          >
            {items.map((item, itemIndex) => {
              const Icon = item.icon;
              const key = `${copyIndex}-${itemIndex}`;
              const isLastItem = itemIndex === items.length - 1;

              if (variant === 'icon') {
                return (
                  <li 
                    key={key} 
                    className="flex-shrink-0 select-none"
                    role="listitem"
                    style={{
                      width: `${ANIMATION_CONFIG.ELEMENT_WIDTH}px`,
                      marginRight: isLastItem ? '64px' : '32px'
                    }}
                  >
                    <div className="flex flex-col items-center gap-3">
                      <div
                        className="w-16 h-16 rounded-full bg-slate-900 text-[#d6fa70] flex items-center justify-center text-2xl transition-transform duration-300"
                        title={item.label}
                      >
                        <Icon aria-hidden="true" />
                      </div>
                      <span className="text-sm font-medium text-slate-700 text-center whitespace-nowrap">
                        {item.label}
                      </span>
                    </div>
                  </li>
                );
              }

              // Image variant
              return (
                <li 
                  key={key} 
                  className="flex-shrink-0 select-none"
                  role="listitem"
                  style={{
                    width: `${ANIMATION_CONFIG.ELEMENT_WIDTH}px`,
                    marginRight: isLastItem ? '64px' : '32px'
                  }}
                >
                  <div className="flex flex-col items-center gap-3">
                    <img
                      src={item.src}
                      alt={item.alt || item.label}
                      className="h-16 object-contain transition-transform duration-300"
                      loading="lazy"
                      decoding="async"
                      draggable={false}
                    />
                    <span className="text-sm font-medium text-slate-700 text-center whitespace-nowrap">
                      {item.label}
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
        )),
      [copyCount, items, variant]
    );

    const handleMouseEnter = useCallback(() => {
      setIsHovered(true);
    }, []);

    const handleMouseLeave = useCallback(() => {
      setIsHovered(false);
    }, []);

    return (
      <div
        ref={containerRef}
        className={cx(
          'relative overflow-x-hidden w-full',
          className
        )}
        style={style}
        role="region"
        aria-label="Logo carousel"
      >
        <style>{`
          div::-webkit-scrollbar {
            display: none;
          }
        `}</style>

        <div
          className="flex will-change-transform select-none relative z-0"
          ref={trackRef}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          style={{
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
          }}
        >
          {logoLists}
        </div>
      </div>
    );
  }
);

LogoSlider.displayName = 'LogoSlider';

export default LogoSlider;
